import type { ChangeEvidence, ChangeKind, Row } from '@contract';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { callAdmin } from '@/lib/api';
import { invalidate } from '@/lib/query';
import { toast } from '@/lib/toast';

/** One way to settle a record: the change request it sends, and what it does to money. */
export interface Resolution {
  value: string;
  label: string;
  /** One line on the money effect. */
  effect: string;
  kind: ChangeKind;
  payload: Row;
  /** A bank reference the staff member must type (money that came back). */
  needsReference?: boolean;
}

/**
 * The shell every Resolve dialog shares: a plain sentence, the key facts, a
 * note, the valid resolutions as a required radio, a reason, and a change
 * request for a second person to approve.
 */
export function ResolveDialog({
  title,
  sentence,
  facts,
  note,
  hint,
  options,
  evidence,
  onClose,
}: Readonly<{
  title: string;
  sentence: string;
  /** `<Fact>` rows. */
  facts: ReactNode;
  /** A warning shown above the choices. */
  note?: string | null;
  /** A muted line above the choices. */
  hint?: string | null;
  options: readonly Resolution[];
  evidence: readonly ChangeEvidence[];
  onClose: () => void;
}>) {
  const navigate = useNavigate();
  const [choice, setChoice] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const picked = options.find((option) => option.value === choice);

  const send = useMutation({
    mutationFn: async () => {
      if (!picked) throw new Error('Choose how this is resolved.');
      const payload: Row = { ...picked.payload };
      if (picked.needsReference) payload.bank_reference = reference.trim();
      return callAdmin('changes.propose', {
        kind: picked.kind,
        payload,
        reason: reason.trim(),
        evidence: [...evidence],
      });
    },
    onSuccess: async (request) => {
      onClose();
      await invalidate('changes.list');
      toast.success('Sent for approval', {
        description: request.summary,
        action: {
          label: 'Open',
          onClick: () =>
            void navigate({
              to: '/approvals/$requestId',
              params: { requestId: request.id },
            }),
        },
      });
    },
  });

  function submit() {
    if (!picked) return setError('Choose how this is resolved.');
    if (picked.needsReference && !reference.trim())
      return setError('Give the bank’s reference.');
    if (reason.trim().length < 8)
      return setError('Give a reason of at least 8 characters.');
    setError(null);
    send.mutate();
  }

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{sentence}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-6 gap-y-2 text-sm">
            {facts}
          </dl>
          {note ? <Alert variant="warning">{note}</Alert> : null}
          {hint ? (
            <p className="text-xs text-muted-foreground">{hint}</p>
          ) : null}

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">
              How is it resolved?
            </legend>
            <RadioGroup
              value={choice}
              onValueChange={(value) => setChoice(String(value))}
            >
              {options.map((option) => (
                <Label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
                >
                  <RadioGroupItem value={option.value} />
                  <span className="flex flex-col gap-0.5">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {option.effect}
                    </span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </fieldset>

          {picked?.needsReference ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="resolve-reference">Bank reference</Label>
              <Input
                id="resolve-reference"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor="resolve-reason">Reason</Label>
            <Textarea
              id="resolve-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              8 to 500 characters. The checker reads it, and it is kept in the
              audit log.
            </p>
          </div>

          <p className="text-sm font-medium">
            Needs a second person to approve.
          </p>
          {(error ?? send.error) ? (
            <Alert variant="destructive">{error ?? send.error?.message}</Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={send.isPending}>
              Send for approval
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Fact({
  label,
  children,
}: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
